/**
 * Survey Templates — list page + create-flow entry points.
 *
 * Supports:
 *   • Empty template creation (opens the editor on a fresh draft)
 *   • Creation from the built-in template library (wizard)
 *   • Score Rated column (derived from backend flag `scoring_enabled`)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label } from '@/components/ui/input';
import {
  Dialog as DialogRaw, DialogContent as DialogContentRaw,
  DialogHeader as DialogHeaderRaw, DialogTitle as DialogTitleRaw,
  DialogDescription as DialogDescriptionRaw, DialogFooter as DialogFooterRaw,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Edit3, Plus, Sparkles, LibraryBig, CheckCircle2, MinusCircle, Copy,
  ShieldCheck, ClipboardList, Trash2, Download, Upload, MapPin,
} from 'lucide-react';

// Cast shadcn dialog exports (.jsx) so TS strict mode accepts children.
type DlgProps = { className?: string; children?: React.ReactNode; [k: string]: unknown };
const Dialog = DialogRaw as unknown as React.ComponentType<DlgProps>;
const DialogContent = DialogContentRaw as unknown as React.ComponentType<DlgProps>;
const DialogHeader = DialogHeaderRaw as unknown as React.ComponentType<DlgProps>;
const DialogTitle = DialogTitleRaw as unknown as React.ComponentType<DlgProps>;
const DialogDescription = DialogDescriptionRaw as unknown as React.ComponentType<DlgProps>;
const DialogFooter = DialogFooterRaw as unknown as React.ComponentType<DlgProps>;

interface Template {
  id: string;
  name: string;
  description: string;
  active: boolean;
  current_published_version?: number;
  latest_version?: number;
  versions_count: number;
  created_at: string;
  scoring_enabled?: boolean;
}

interface LibraryItem {
  key: string;
  name: string;
  description: string;
  category: string;
  is_scored: boolean;
  seed_hint: string;
  sections_count: number;
  questions_count: number;
}

// -------------------------------------------------------------------------
export const TemplatesPage: React.FC = () => {
  const [items, setItems] = useState<Template[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Template | null>(null);
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();
  const { role } = useAuth();
  const canDelete = role?.key === 'super_admin' || role?.key === 'master_admin';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const toggleSelect = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const downloadBundle = (bundle: unknown, filename: string): void => {
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportOne = async (t: Template): Promise<void> => {
    try {
      const r = await api.get(`/templates/${t.id}/export`);
      downloadBundle(r.data, `${t.name.replace(/[^a-z0-9]+/gi, '_')}.raptor-template.json`);
      toast.success(`Exported "${t.name}"`);
    } catch { toast.error('Export failed'); }
  };

  const exportSelected = async (): Promise<void> => {
    try {
      const r = await api.post('/templates/export', { template_ids: Array.from(selected) });
      downloadBundle(r.data, `survey_templates_${selected.size}.raptor-bundle.json`);
      toast.success(`Exported ${selected.size} template(s)`);
      setSelected(new Set());
    } catch { toast.error('Bulk export failed'); }
  };

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const bundle = JSON.parse(await file.text());
      const r = await api.post<{ imported: number }>('/templates/import', bundle);
      toast.success(`Imported ${r.data.imported} template(s) into this portal`);
      await load();
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { detail?: string } } };
      toast.error(e2?.response?.data?.detail ?? 'Import failed — is this a valid template bundle?');
    } finally { setImporting(false); }
  };

  const load = async (): Promise<void> => {
    try {
      const r = await api.get<Template[]>('/templates');
      setItems(r.data ?? []);
    } catch (err) {
      toast.error('Failed to load templates');
    }
  };
  useEffect(() => { void load(); }, []);

  const confirmDelete = async (): Promise<void> => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/templates/${toDelete.id}`);
      toast.success(`Deleted "${toDelete.name}"`);
      setToDelete(null);
      void load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error ?? 'Could not delete template');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Survey Templates</h2>
          <p className="text-sm text-raptor-navy/60 font-body">
            Versioned, immutable at issue time. Editing a published template creates a new draft version.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button data-testid="export-selected-btn" variant="outline" onClick={() => void exportSelected()}>
              <Download size={16} className="mr-1" /> Export selected ({selected.size})
            </Button>
          )}
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => void onImportFile(e)} data-testid="import-file-input" />
          <Button data-testid="import-template-btn" variant="outline" disabled={importing} onClick={() => fileRef.current?.click()}>
            <Upload size={16} className="mr-1" /> {importing ? 'Importing…' : 'Import'}
          </Button>
          <Button data-testid="new-empty-template-btn" variant="outline" onClick={() => setEmptyOpen(true)}>
            <Plus size={16} className="mr-1" /> New empty
          </Button>
          <Button data-testid="new-from-library-btn" variant="gradient" onClick={() => setLibraryOpen(true)}>
            <LibraryBig size={16} className="mr-1" /> From library
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
              <tr>
                <th className="py-3 px-4 text-left w-8"></th>
                <th className="py-3 px-4 text-left">ID</th>
                <th className="py-3 px-4 text-left">Name</th>
                <th className="py-3 px-4 text-left">Versions</th>
                <th className="py-3 px-4 text-left">Published</th>
                <th className="py-3 px-4 text-left">Status</th>
                <th className="py-3 px-4 text-left" title="Whether this template feeds the Risk Ratings engine">
                  Score Rated
                </th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-raptor-navy/50 font-body">
                    No templates yet. Click <b>+ New empty</b>, <b>+ From library</b>, or <b>Import</b> to create your first template.
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <tr key={t.id} className="border-t border-border/60 hover:bg-raptor-neutral/20" data-testid={`template-row-${t.id}`}>
                  <td className="py-3 px-4">
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} data-testid={`select-template-${t.id}`} aria-label={`Select ${t.name}`} />
                  </td>
                  <td className="py-3 px-4 font-tabular">{t.id}</td>
                  <td className="py-3 px-4 font-semibold">
                    {t.name}
                    <div className="text-xs font-body text-raptor-navy/60 line-clamp-1">{t.description}</div>
                  </td>
                  <td className="py-3 px-4 font-tabular">{t.versions_count}</td>
                  <td className="py-3 px-4 font-tabular">{t.current_published_version ? `v${t.current_published_version}` : '—'}</td>
                  <td className="py-3 px-4">{t.active ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Inactive</Badge>}</td>
                  <td className="py-3 px-4" data-testid={`template-score-flag-${t.id}`}>
                    {t.scoring_enabled ? (
                      <Badge variant="blue" className="gap-1"><ShieldCheck size={12} /> Yes</Badge>
                    ) : (
                      <Badge variant="muted" className="gap-1"><MinusCircle size={12} /> No</Badge>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => nav(`/insurer/templates/${t.id}`)} data-testid={`open-template-${t.id}`}>
                        <Edit3 size={14} className="mr-1" /> Open
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void exportOne(t)} data-testid={`export-template-${t.id}`} title="Export this template as a JSON bundle">
                        <Download size={14} />
                      </Button>
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => setToDelete(t)}
                          data-testid={`delete-template-${t.id}`}
                          title="Delete template (Super Admin only)"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {libraryOpen && <LibraryWizard onClose={() => setLibraryOpen(false)} onCreated={(tid) => { setLibraryOpen(false); nav(`/insurer/templates/${tid}`); }} />}
      {emptyOpen && <EmptyTemplateDialog onClose={() => setEmptyOpen(false)} onCreated={(tid) => { setEmptyOpen(false); nav(`/insurer/templates/${tid}`); }} />}

      {toDelete && (
        <Dialog open onOpenChange={(o: boolean) => { if (!o) setToDelete(null); }}>
          <DialogContent className="max-w-md" data-testid="delete-template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 size={18} /> Delete template
              </DialogTitle>
              <DialogDescription>
                This permanently deletes <b>{toDelete.name}</b> ({toDelete.id}) and all {toDelete.versions_count} version(s).
                This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleting} data-testid="delete-cancel-btn">Cancel</Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


// -------------------------------------------------------------- Library wizard
const LibraryWizard: React.FC<{ onClose: () => void; onCreated: (tid: string) => void }> = ({ onClose, onCreated }) => {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [selected, setSelected] = useState<LibraryItem | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ items: LibraryItem[] }>('/templates/library')
      .then((r) => setItems(r.data.items ?? []))
      .catch(() => toast.error('Could not load template library'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selected) {
      setName(selected.name);
      setDescription(selected.description);
    }
  }, [selected]);

  const create = async (): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post<{ id: string }>(`/templates/from-library/${selected.key}`, { name, description });
      toast.success(`Template "${name}" created`);
      onCreated(r.data.id);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error ?? 'Could not create template');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="library-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LibraryBig size={20} className="text-raptor-blue" /> Choose a starter template
          </DialogTitle>
          <DialogDescription>
            Pick a prebuilt survey to duplicate into your tenant. You can freely rename, edit, and version the copy afterwards — the library entry stays untouched.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-raptor-navy/60">Loading library…</div>
        ) : (
          <div className="grid gap-3">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                data-testid={`library-item-${it.key}`}
                onClick={() => setSelected(it)}
                className={`text-left p-4 rounded-lg border transition-all ${
                  selected?.key === it.key
                    ? 'border-raptor-blue bg-raptor-blue/5 ring-2 ring-raptor-blue/30'
                    : 'border-border/60 hover:bg-raptor-neutral/40'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-headline text-raptor-navy">{it.name}</span>
                      <Badge variant="muted" className="text-[10px]">{it.category}</Badge>
                      {it.is_scored
                        ? <Badge variant="blue" className="gap-1 text-[10px]"><ShieldCheck size={11} /> Score Rated</Badge>
                        : <Badge variant="muted" className="text-[10px]">Report only</Badge>}
                    </div>
                    <div className="text-sm text-raptor-navy/70 mt-1">{it.description}</div>
                    {it.seed_hint && (
                      <div className="text-[11px] text-raptor-navy/50 mt-1 italic">{it.seed_hint}</div>
                    )}
                    <div className="text-xs text-raptor-navy/60 mt-2 flex items-center gap-3">
                      <span className="flex items-center gap-1"><ClipboardList size={12} /> {it.sections_count} sections</span>
                      <span>·</span>
                      <span>{it.questions_count} questions</span>
                    </div>
                  </div>
                  {selected?.key === it.key && <CheckCircle2 className="text-raptor-blue" size={20} />}
                </div>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <div className="mt-4 rounded-md border border-border/60 bg-raptor-neutral/30 p-3 space-y-3">
            <div className="grid gap-2">
              <Label>Name of your new template</Label>
              <Input data-testid="library-name-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Input data-testid="library-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="gradient"
            disabled={!selected || busy || !name.trim()}
            onClick={() => void create()}
            data-testid="library-create-btn"
          >
            <Copy size={14} className="mr-1" /> {busy ? 'Creating…' : 'Create from selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------- Empty template dialog
const EmptyTemplateDialog: React.FC<{ onClose: () => void; onCreated: (tid: string) => void }> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [geoMode, setGeoMode] = useState<'strict' | 'capture_only'>('strict');
  const [threshold, setThreshold] = useState(200);
  const [busy, setBusy] = useState(false);

  const create = async (): Promise<void> => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const schema = {
        sections: [{
          section_code: `SEC${Date.now()}`, number: '1', title: 'New section', description: '', questions: [],
        }],
        signature_slots: [],
        photo_gallery_enabled: false,
        photo_min_count: 0,
        geo_mode: geoMode,
        geo_threshold_m: threshold,
        geofence_required: geoMode === 'strict',
      };
      const r = await api.post<{ id: string }>('/templates', { name, description, schema });
      toast.success('Template created — opening editor…');
      onCreated(r.data.id);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error ?? 'Could not create template');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="empty-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-raptor-blue" /> New empty template</DialogTitle>
          <DialogDescription>
            Start from scratch. You'll be dropped into the editor with a single blank section ready to configure.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label>Template name</Label>
            <Input data-testid="empty-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Custom Site Inspection" />
          </div>
          <div className="grid gap-2">
            <Label>Description</Label>
            <Input data-testid="empty-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" />
          </div>
          <div className="grid gap-2 rounded-md border border-border/60 bg-raptor-neutral/30 p-3">
            <Label className="flex items-center gap-1"><MapPin size={14} /> On-site location policy</Label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="radio" name="new-geo" className="mt-0.5" checked={geoMode === 'strict'} onChange={() => setGeoMode('strict')} data-testid="new-geo-strict" />
              <span><b>Strict</b> — capture + confirm address and check distance to the site (outside the limit is flagged, never blocked). Site must be geocoded to issue.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="radio" name="new-geo" className="mt-0.5" checked={geoMode === 'capture_only'} onChange={() => setGeoMode('capture_only')} data-testid="new-geo-capture" />
              <span><b>Capture-only</b> — record location + address, no distance check.</span>
            </label>
            {geoMode === 'strict' && (
              <div className="grid gap-1 max-w-[200px] mt-1">
                <Label className="text-xs">Allowed distance (metres)</Label>
                <Input type="number" min={10} value={threshold} onChange={(e) => setThreshold(Math.max(10, parseInt(e.target.value || '0', 10) || 0))} data-testid="new-geo-threshold" />
              </div>
            )}
            <div className="text-[11px] text-raptor-navy/50">You can change this later in Template settings.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="gradient" disabled={busy} onClick={() => void create()} data-testid="empty-create-btn">
            {busy ? 'Creating…' : 'Create & edit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatesPage;
